import { FullPageScreen } from "@/lib/Components/Screens/FullPageScreen";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { RelocationMultiTable } from "@/app/Relocations/Components/RelocationMultiTable";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { RelocationType } from "@/gql/graphql";
import { LinkButton } from "@/components/catalyst/button";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "use-intl";
import { motion } from "framer-motion";
import { useCallback, useState } from "react";
import {
  parseRelocationTableSearch,
  relocationTableSearchFromState,
  relocationTableStateFromSearch,
} from "@/app/Relocations/Utils/relocationTableSearchParams";
import { useSearchBackedState } from "@/lib/Hooks/useSearchBackedState";

export function RelocationTableScreen() {
  const t = useTranslations("navigation");
  const { supplierId } = useGlobalSupplier();
  const search = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/relocations",
  });
  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/relocations",
  });
  const [state, setState] = useSearchBackedState({
    search,
    parseSearch: parseRelocationTableSearch,
    fromSearch: relocationTableStateFromSearch,
    writeSearch: (newState, { replace }) => {
      navigate({
        search: (old) => ({
          ...old,
          ...relocationTableSearchFromState(newState),
        }),
        replace,
      });
    },
  });

  const [spotlight, setSpotlight] = useState<{ x: number; y: number } | null>(
    null,
  );

  const measureButton = useCallback((el: HTMLSpanElement | null) => {
    if (el) {
      const rect = el.getBoundingClientRect();
      setSpotlight({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }
  }, []);

  const dismissHighlight = () => {
    navigate({
      search: (old) => ({ ...old, highlightImport: undefined }),
    });
  };

  return (
    <FullPageScreen>
      {search.highlightImport && spotlight ? (
        <motion.div
          onClick={dismissHighlight}
          className="fixed inset-0 z-40 cursor-pointer"
          style={{
            background: `radial-gradient(circle at ${spotlight.x}px ${spotlight.y}px, transparent 60px, rgba(9,9,11,0.55) 140px, rgba(9,9,11,0.72) 100%)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      ) : null}
      <RelocationMultiTable
        supplierId={supplierId ?? undefined}
        tableId="relocations"
        type={[RelocationType.Relocation]}
        state={state}
        extraActions={
          <span
            ref={measureButton}
            className={search.highlightImport ? "relative z-50" : undefined}
          >
            <LinkButton
              outline
              LeadingIcon={ArrowUpTrayIcon}
              className={
                search.highlightImport
                  ? "shadow-lg shadow-amber-300/60 ring-4 ring-amber-400 ring-offset-2"
                  : undefined
              }
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/import"
              params={(prev) => prev}
            >
              {t("import")}
            </LinkButton>
          </span>
        }
        onStateChange={setState}
      />
    </FullPageScreen>
  );
}
